import { useEffect } from 'react';
import { Form, Input, InputNumber, Modal, Switch } from 'antd';
import type { CustomerCareGroup, UpsertCustomerCareGroupPayload } from './api';

interface FormValues {
  name: string;
  url: string;
  sortOrder: number;
  isEnabled: boolean;
}

interface Props {
  group: CustomerCareGroup | null;
  isCreating: boolean;
  submitting: boolean;
  onSubmit: (payload: UpsertCustomerCareGroupPayload) => void;
  onClose: () => void;
}

export function CustomerCareGroupFormModal({
  group,
  isCreating,
  submitting,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const open = group !== null || isCreating;

  useEffect(() => {
    if (!open) return;
    if (group) {
      form.setFieldsValue({
        name: group.name,
        url: group.url,
        sortOrder: group.sortOrder,
        isEnabled: group.isEnabled,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0, isEnabled: true });
    }
  }, [group, open, form]);

  return (
    <Modal
      open={open}
      title={isCreating ? 'Thêm nhóm chăm sóc' : 'Sửa nhóm chăm sóc'}
      okText="Lưu"
      cancelText="Hủy"
      width={520}
      confirmLoading={submitting}
      destroyOnHidden
      onCancel={onClose}
      onOk={() => {
        form
          .validateFields()
          .then((values) =>
            onSubmit({
              name: values.name.trim(),
              url: values.url.trim(),
              sortOrder: values.sortOrder ?? 0,
              isEnabled: values.isEnabled ?? true,
            }),
          )
          .catch(() => {});
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label="Tên nhóm"
          rules={[
            { required: true, whitespace: true, message: 'Chưa nhập tên nhóm.' },
            { max: 80, message: 'Tên nhóm quá dài (tối đa 80 ký tự).' },
          ]}
        >
          <Input placeholder="Ví dụ: Nhóm chăm sóc Hà Nội" maxLength={80} />
        </Form.Item>
        <Form.Item
          name="url"
          label="Link nhóm Zalo"
          extra="Khách xác nhận trên app rồi được mở link này."
          rules={[
            { required: true, whitespace: true, message: 'Chưa nhập link nhóm.' },
            { max: 500, message: 'Link nhóm quá dài (tối đa 500 ký tự).' },
            {
              pattern: /^https?:\/\/.+/i,
              message: 'Link nhóm phải bắt đầu bằng https:// hoặc http://.',
            },
          ]}
        >
          <Input placeholder="https://zalo.me/g/..." maxLength={500} />
        </Form.Item>
        <Form.Item
          name="sortOrder"
          label="Thứ tự"
          extra="Số nhỏ hơn hiện trước. Cùng số thì xếp theo tên."
          rules={[{ required: true, type: 'number', min: 0, message: 'Thứ tự không được âm.' }]}
        >
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="isEnabled"
          label="Đang bật"
          valuePropName="checked"
          extra="Tắt thì khách không thấy nhóm này. Bật lại khi cần, không phải tạo mới."
        >
          <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
